import EventEmitter from "eventemitter3";
import LegacyDispatcher from "~/lib/legacyDispatcher";
import Database from "~/lib/Database";
import string from "~/lib/string";
import { postJson, putJson, whenAll } from "~/lib/http";

type Project = {
  id: string;
};

type Settings = Record<string, any>;

class UserStore {
  pipelineSettings: Settings;
  eventEmitter: EventEmitter;

  constructor() {
    this.pipelineSettings = {};
    this.eventEmitter = new EventEmitter();

    this.registerWithDispatcher();
  }

  emitChange() {
    return this.eventEmitter.emit("change");
  }

  updateProjectSettings(project: Project, settings: Settings) {
    const pipelineSettings: Record<string, any> = {};
    Object.entries(settings).forEach(([key, value]: [any, any]) => {
      pipelineSettings[string.underscore(key)] = value;
    });

    const promise = putJson<void>(`/user/pipeline_settings/${project.id}`, {
      pipeline_settings: pipelineSettings,
    })
      .then(() => {
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("pipeline_settings:update:success", {
          project: { id: project.id },
          settings: settings,
        });
      })
      .catch(() => {
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("pipeline_settings:update:error", {
          project: { id: project.id },
          settings: settings,
        });
      });

    // Return a promise
    return whenAll([promise]);
  }

  starProjectBranch(project: Project, branch: string) {
    const promise = postJson<void>(`/user/pipeline_settings/${project.id}/star`, { branch })
      .then(() => {
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("pipeline_settings:update:success", {
          project: { id: project.id },
        });
      })
      .catch(() => {
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("pipeline_settings:update:error", {
          project: { id: project.id },
        });
      });

    // Return a promise
    return whenAll([promise]);
  }

  unstarProjectBranch(project: Project, branch: string) {
    const promise = postJson<void>(`/user/pipeline_settings/${project.id}/unstar`, { branch })
      .then(() => {
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("pipeline_settings:update:success", {
          project: { id: project.id },
        });
      })
      .catch(() => {
        // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
        LegacyDispatcher.emit("pipeline_settings:update:error", {
          project: { id: project.id },
        });
      });

    // Return a promise
    return whenAll([promise]);
  }

  registerWithDispatcher() {
    let existingSettings;
    LegacyDispatcher.on("pipeline_settings:update", ({ project, settings }) => {
      existingSettings = this.pipelineSettings[project.id];
      const updatedSettings = (this.pipelineSettings[project.id] = Object.assign(
        existingSettings,
        settings,
      ));

      this.updateProjectSettings(project, updatedSettings);

      this.emitChange();
    });

    LegacyDispatcher.on("pipeline_settings:update:success", () => this.emitChange());

    LegacyDispatcher.on("pipeline_settings:update:error", () => this.emitChange());

    LegacyDispatcher.on("project_branch:star", ({ project, branch }) => {
      existingSettings = this.pipelineSettings[project.id];
      existingSettings.starredBranches.push(branch);
      existingSettings.unstarredBranches = existingSettings.unstarredBranches.filter(
        (unstarredBranch) => unstarredBranch !== branch,
      );

      this.starProjectBranch(project, branch);

      this.emitChange();
    });

    LegacyDispatcher.on("project_branch:unstar", ({ project, branch }) => {
      existingSettings = this.pipelineSettings[project.id];
      existingSettings.starredBranches = existingSettings.starredBranches.filter(
        (starredBranch) => starredBranch !== branch,
      );
      existingSettings.unstarredBranches.push(branch);

      this.unstarProjectBranch(project, branch);

      this.emitChange();
    });
  }

  bootstrap(project: Project, settings: Settings) {
    return (this.pipelineSettings[project.id] = Database.parse(settings));
  }

  addChangeListener(callback: any) {
    return this.eventEmitter.addListener("change", callback);
  }

  removeChangeListener(callback: any) {
    return this.eventEmitter.removeListener("change", callback);
  }

  getEmailSettings(project: Project) {
    return this.pipelineSettings[project.id];
  }

  getStarredBranches(project: Project) {
    let settings;

    if ((settings = this.pipelineSettings[project.id])) {
      return settings.starredBranches;
    }

    return [];
  }
}

export default new UserStore();
